import { useEffect, useState, useContext, useMemo } from "react"
import { DateTime } from "luxon"

//Notifications modules
import {
  noteApiListNotificationsPOST,
  noteApiMarkAsReadPut,
  noteApiMarkAllAsReadPut,
  noteApiMarkAsUnreadPut,
  noteApiListEventTypesGet,
} from "../notificationsApi"
import NotificationsFilters from "./NotificationsFilters"
import {
  anyNotificationHasTags,
  notificationHasParticipantTag,
  notificationHasStaffTag,
  notificationHasTags,
  replaceNotificationTags,
} from "../notificationsTags"
import { GET_NOTIFICATION_USERS } from "../notificationsQueries"

//Notifications resources:
import markAsReadIcon from "../resources/mark_as_read_icon.svg"
import markAsUnreadIcon from "../resources/mark_as_unread_icon.svg"

//styles
import noteStyle from "../../../styles/notifications/NotificationsForm.module.css"

// Context
import { UserContext } from "../../../context/userContext"

//prime react
import { Accordion, AccordionTab } from "primereact/accordion"
import { ProgressSpinner } from "primereact/progressspinner"
import { Paginator } from "primereact/paginator"
import { confirmDialog } from "primereact/confirmdialog"
import { Tooltip } from "primereact/tooltip"
import { Button } from "primereact/button"
import { Tag } from "primereact/tag"
import { Badge } from "primereact/badge"
import { classNames } from "primereact/utils"

//Helpers
import { LogDebug, LogError } from "../../../helpers/logger"
import { displayLocalDateTimeFromIso } from "../../../helpers/displayDates"
import { useLazyQuery } from "@apollo/client"

import { GET_PARTICIPANT } from "../notificationsQueries"
import NotificationCard from "./NotificationCard"
import {
  isEnterpriseAdmin,
  isPartnerAdmin,
} from "../../../helpers/staffRoleHelpers"

export default function NotificationsTab({
  organizationList,
  getOrganizationName,
  onRefreshBellCounter,
  onShowError,
  onHandleActionURL,
}) {
  const userCtx = useContext(UserContext)
  const notificationServiceStaffId = "-9" //Service staff account: RecoveryLInk Microservices

  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [notifications, setNotifications] = useState(null)
  const [eventTypes, setEventTypes] = useState(null)
  const [refresh, setRefresh] = useState(new Date().getTime() - 10000)
  const [lastRefresh, setLastRefresh] = useState(new Date().getTime() - 10000)

  // Filters //
  const defaultFilters = {
    search: "",
    eventType: "All",
    createDateStart: userCtx.session?.user?.staff_create_date
      ? DateTime.fromISO(userCtx.session.user.staff_create_date).toJSDate()
      : undefined,
    createDateEnd: "",
    readDateStart: "",
    readDateEnd: "",
    sort: "newest",
    includeRead: false,
    includeUnread: true,
    //If this is a referral partner admin, or an enterprise admin, we want to pull notifications across all organizations,
    // so we set the organization_id to "All" which is a special value that the API recognizes and handles accordingly
    groupIds: [],
  }

  const [filters, setFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)

  const [getNotificationUsers] = useLazyQuery(GET_NOTIFICATION_USERS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: notificationServiceStaffId, //Service staff account: RecoveryLInk Microservices
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const [getParticipant] = useLazyQuery(GET_PARTICIPANT, {
    variables: {
      staff_id: undefined,
      participant_id: undefined,
      organization_id: undefined,
    },
  })

  async function findParticipant(participant_id, organization_id) {
    let result = undefined

    try {
      const participant = await getParticipant({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          participant_id: participant_id,
          organization_id: organization_id,
          participant_alias: participant_id,
        },
      })

      if (!!participant.data?.participant) {
        result = participant.data.participant
      } else if (!!participant.data?.search) {
        const searchResult = participant.data.search.filter(
          (result) => result.participant_alias === participant_id,
        )
        if (searchResult.length > 0) {
          result = searchResult[0]
        }
      }
    } catch (error) {
      LogError("Unable to search for participant", error)
      result = undefined
    }

    return result
  }

  async function checkParticipantList(notification, participantList) {
    if (
      !!notification.caseId &&
      !participantList.some(
        (part) =>
          part.participant_id === notification.caseId ||
          part.participant_alias === notification.caseId,
      )
    ) {
      let participantSearch = await findParticipant(
        notification.caseId,
        notification.groupId,
      )

      if (!!participantSearch) {
        participantList = [...participantList, participantSearch]
      } else {
        LogError("Participant not found")
      }
    }

    return participantList
  }

  async function getNotifications(page, currentPageSize) {
    // Limit refreshes

    if (lastRefresh !== null && new Date().getTime() - lastRefresh < 1000)
      return

    setLastRefresh(new Date().getTime())

    setLoading(true)

    let groupIds = appliedFilters.groupIds

    //If the filter groupIds is empty, it means we want to pull notifications for all organizations the user has access to, so we set groupIds to "All" which is a special value that the API recognizes and handles accordingly
    if (appliedFilters.groupIds.length === 0 && organizationList?.length > 0) {
      groupIds = [
        ...organizationList.map((x) => x.organization_id),
        ...organizationList.map((x) => x.organization_alias),
      ]
    } else {
      groupIds = [
        ...appliedFilters.groupIds,
        ...organizationList
          .filter((org) =>
            appliedFilters.groupIds.includes(org.organization_id),
          )
          .map((x) => x.organization_alias),
      ]
    }

    try {
      const res = await noteApiListNotificationsPOST(
        userCtx.tokens.getNotificationToken(),
        userCtx,
        groupIds,
        combineReadAndUnreadFilters(),
        appliedFilters.eventType,
        appliedFilters.createDateStart,
        appliedFilters.createDateEnd,
        appliedFilters.readDateStart,
        appliedFilters.readDateEnd,
        appliedFilters.search,
        appliedFilters.sort,
        page || 1,
        currentPageSize || pageSize,
      )

      if (res) {
        if (anyNotificationHasTags(res.items)) {
          const result = await getNotificationUsers()
          let participantList = result.data?.participants || []
          let staffList = result.data?.staff || []

          //search for participants
          for (let item in res.items) {
            if (notificationHasParticipantTag(res.items[item])) {
              participantList = await checkParticipantList(
                res.items[item],
                participantList,
              )
            }
          }

          res.items = await Promise.all(
            res.items.map(async (notification) => {
              if (notificationHasTags(notification)) {
                return await replaceNotificationTags(
                  notification,
                  participantList,
                  undefined,
                  undefined,
                  staffList,
                  undefined,
                )
              } else {
                return notification
              }
            }),
          )
        }

        setNotifications(res)
        onRefreshBellCounter(new Date().getTime())
      }
      LogDebug("Notifications", res)
    } catch (error) {
      console.error("Unable to list notifications", error)
      onShowError("Unable to list notifications. See console for details.")
    }
    setLoading(false)
  }

  async function markAllAsRead() {
    if (!!notifications) {
      setLoading(true)

      await noteApiMarkAllAsReadPut(
        userCtx.tokens.getNotificationToken(),
        userCtx,
      )

      setNotifications(null)
      getNotifications()

      setLoading(false)
    }
  }

  async function markAsRead(notificationId) {
    setLoading(true)

    try {
      await noteApiMarkAsReadPut(
        userCtx.tokens.getNotificationToken(),
        notificationId,
      )
      getNotifications()
      LogDebug(`Notification marked as read (ID: ${notificationId})`)
    } catch (error) {
      LogError(
        `Unable to mark notification as read (id: ${notificationId})`,
        error,
      )
      onShowError(
        `Unable to mark notification as read (id: ${notificationId}). See console for details.`,
      )
    }
    setLoading(false)
  }

  async function markAsUnread(notificationId) {
    setLoading(true)

    try {
      await noteApiMarkAsUnreadPut(
        userCtx.tokens.getNotificationToken(),
        notificationId,
      )
      getNotifications()
      LogDebug(`Notification marked as unread (ID: ${notificationId})`)
    } catch (error) {
      LogError(
        `Unable to mark notification as unread (id: ${notificationId})`,
        error,
      )
      onShowError(
        `Unable to mark notification as unread (id: ${notificationId}). See console for details.`,
      )
    }
    setLoading(false)
  }

  async function getEventTypes() {
    setLoading(true)
    try {
      const res = await noteApiListEventTypesGet(
        userCtx.tokens.getNotificationToken(),
      )

      if (res) {
        setEventTypes(res)
      }
      LogDebug("EventTypes", res)
    } catch (error) {
      LogError("Unable to list event types", error)
      onShowError("Unable to list event types. See console for details.")
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!notifications) {
      getNotifications().catch((error) => {
        LogError("Error fetching notifications", error)
      })
    } else if (!eventTypes) {
      getEventTypes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, eventTypes])

  useEffect(() => {
    if (notifications) {
      getNotifications()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh])

  useEffect(() => {
    if (!!userCtx.notification_refresh) {
      setRefresh(new Date().getTime())
      userCtx.setNotificationRefresh(undefined)
    }
  }, [userCtx])

  useEffect(() => {
    if (notifications !== null) {
      getNotifications()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters])

  useEffect(() => {
    LogDebug("Filters", filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  function combineReadAndUnreadFilters() {
    const { includeRead, includeUnread } = appliedFilters

    if (includeRead && includeUnread) return null
    else if (includeRead && !includeUnread) return true
    else return false
  }

  const filterHeader = () => {
    return (
      <div className={noteStyle.noteFilterHeaderGridContainer}>
        <div className={noteStyle.noteFilterHeader}>
          <label>Filters</label>
        </div>
        <div className={noteStyle.noteFilterButton}>
          <button
            type="button"
            className="btn"
            style={{
              float: "right",
              width: 40,
              height: 33,
              display: "flex",
              padding: "4px 9px",
            }}
            onClick={(e) => {
              e.stopPropagation()
              setRefresh(new Date().getTime())
            }}
          >
            <span
              className={`material-icons  p-overlay-badge ${noteStyle.NoteMarkRead}`}
            >
              {"autorenew"}
            </span>
          </button>
          <button
            type="button"
            className="btn t-tip"
            data-pr-tooltip="Mark All Notifications As Read"
            data-pr-position="bottom"
            style={{
              float: "right",
              width: 40,
              height: 33,
              display: "flex",
              padding: "4px 9px",
              marginRight: "10px",
            }}
            onClick={(e) => {
              e.stopPropagation()
              confirmDialog({
                message:
                  "Do you want to mark all unread notifications as read?",
                header: "Mark all as read",
                accept: () => markAllAsRead(),
              })
            }}
          >
            <span
              className={`material-icons  p-overlay-badge ${noteStyle.NoteMarkRead}`}
            >
              {"done_all"}
            </span>
          </button>
        </div>
      </div>
    )
  }

  const onPageChange = (event) => {
    if (pageSize !== event.rows) {
      setPageSize(event.rows)
    }

    getNotifications(event.page + 1, event.rows)
  }

  return (
    <div>
      <Tooltip target=".t-tip" />
      <div className={noteStyle.addLargeSpace}>
        <Accordion className={noteStyle.noteAccordion}>
          <AccordionTab header={filterHeader()}>
            <NotificationsFilters
              defaultFilters={defaultFilters}
              organizationList={organizationList}
              filters={filters}
              onSetFilters={setFilters}
              onApplyFilters={setAppliedFilters}
              eventTypes={eventTypes}
            />
          </AccordionTab>
        </Accordion>
      </div>
      {loading ? (
        <div style={{ textAlign: "center" }}>
          <ProgressSpinner />
        </div>
      ) : (
        <div className={noteStyle.noteAccordionContainer}>
          {notifications && notifications.items.length > 0 ? (
            <>
              <Accordion
                className={noteStyle.noteAccordion}
                style={{ marginTop: "0.6rem", marginBottom: "0.6rem" }}
              >
                {notifications
                  ? notifications.items.map((x) => {
                      return NotificationCard({
                        notification: x,
                        getOrganizationName: getOrganizationName,
                        onMarkAsRead: markAsRead,
                        onMarkAsUnread: markAsUnread,
                        onActionButtonClick: onHandleActionURL,
                        onExpandDescription: () => {
                          setNotifications((prev) => ({
                            ...prev,
                            items: prev.items.map((n) =>
                              n.id === x.id
                                ? { ...n, descriptionExpanded: true }
                                : n,
                            ),
                          }))
                        },
                        onCollapseDescription: () => {
                          setNotifications((prev) => ({
                            ...prev,
                            items: prev.items.map((n) =>
                              n.id === x.id
                                ? { ...n, descriptionExpanded: false }
                                : n,
                            ),
                          }))
                        },
                      })
                    })
                  : null}
              </Accordion>
              {notifications ? (
                <Paginator
                  first={notifications.pageSize * (notifications.pageNum - 1)}
                  rows={notifications.pageSize}
                  totalRecords={notifications.numTotal}
                  onPageChange={onPageChange}
                  rowsPerPageOptions={[5, 10, 20, 30]}
                />
              ) : null}
            </>
          ) : (
            <div style={{ textAlign: "center" }}>No notifications found</div>
          )}
        </div>
      )}
    </div>
  )
}
